import Api from '@/services/Api';

import {
  getCookie,
  setCookie,
  deleteCookie,
  slugify,
} from '@/js/cookies';

const COOKIE_PREFIX = 'vp_video_';
const COOKIE_DAYS = 14;

function makeKey(video, email) {
  const videoKey = slugify(video?.name || 'default');
  const userKey = slugify(email || 'anon');
  return `${COOKIE_PREFIX}${videoKey}__u_${userKey}`;
}

function readCookieFor(video, email, fallbackMax = 2) {
  const raw = getCookie(makeKey(video, email));
  if (!raw) return { showCount: 0, maxShows: fallbackMax };
  try {
    const parsed = JSON.parse(raw);
    return {
      showCount: Number(parsed.showCount) || 0,
      maxShows: Number(parsed.maxShows) || fallbackMax,
    };
  } catch {
    return { showCount: 0, maxShows: fallbackMax };
  }
}

function writeCookieFor(video, email, payload) {
  const data = {
    showCount: Number(payload.showCount) || 0,
    maxShows: Number(payload.maxShows) || 0,
    updatedAt: new Date().toISOString(),
  };
  setCookie(makeKey(video, email), JSON.stringify(data), COOKIE_DAYS);
}

function clearCookieFor(video, email) {
  deleteCookie(makeKey(video, email));
}

export default {
  namespaced: true,
  state: {
    currentVideo: [], // { name, url, ... }
    isRegistroMuestras: false,
    isFacturas: false,
    isIncidencias: false,
    periodIndex: null,
    videoIndex: null,
    showCount: 0,
    maxShows: 2,
    activeUserEmail: null, // <-- NUEVO
  },
  getters: {
    hasRemainingShows: (state) => state.showCount < state.maxShows,
  },
  actions: {
    // Llamar tras login o cuando cambie el usuario
    setActiveUserEmail({ commit, dispatch, state }, email) {
      commit('setActiveUserEmail', email);
      if (state.currentVideo) {
        dispatch('initFromCookie', { email });
      }
    },

    async fetchUrlPeriodVideos(
      { commit, dispatch, state },
      codIdioma = 'es'
    ) {
      try {
        // Llama al backend pasando el idioma recibido
        const xhr = await Api.getVideoPeriod(codIdioma);
        const response = JSON.parse(xhr.response);

        commit('setCurrentVideo', response.current_video);
        commit('setTourFlags', {
          isRegistroMuestras: response.is_registro_muestras,
          isFacturas: response.is_facturas,
          isIncidencias: response.is_incidencias,
          periodIndex: response.period_index,
          videoIndex: response.video_index,
        });
        dispatch('initFromCookie', { email: state.activeUserEmail });
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    initFromCookie({ state, commit }, { email } = {}) {
      const em = email ?? state.activeUserEmail;
      const { showCount, maxShows } = readCookieFor(
        state.currentVideo,
        em,
        state.maxShows
      );
      commit('setCounters', { showCount, maxShows });
      writeCookieFor(state.currentVideo, em, { showCount, maxShows }); // garantiza existencia
    },

    incrementShowCount({ state, commit }, { email } = {}) {
      const em = email ?? state.activeUserEmail;
      const { showCount, maxShows } = readCookieFor(
        state.currentVideo,
        em,
        state.maxShows
      );
      const next = Math.min(showCount + 1, maxShows);
      commit('setCounters', { showCount: next, maxShows });
      writeCookieFor(state.currentVideo, em, {
        showCount: next,
        maxShows,
      });
    },

    setMaxShowCount({ state, commit }, { email } = {}) {
      const em = email ?? state.activeUserEmail;
      const { maxShows } = readCookieFor(
        state.currentVideo,
        em,
        state.maxShows
      );
      commit('setCounters', { showCount: maxShows, maxShows });
      writeCookieFor(state.currentVideo, em, {
        showCount: maxShows,
        maxShows,
      });
    },

    // Si alguna vez necesitas ponerlo a 0 manualmente (no en login)
    resetCounter({ state, commit }, { email } = {}) {
      const em = email ?? state.activeUserEmail;
      commit('setCounters', {
        showCount: 0,
        maxShows: state.maxShows,
      });
      writeCookieFor(state.currentVideo, em, {
        showCount: 0,
        maxShows: state.maxShows,
      });
    },

    setCurrentVideo({ commit, dispatch, state }, value) {
      commit('setCurrentVideo', value);
      dispatch('initFromCookie', { email: state.activeUserEmail });
    },

    resetData({ state, commit }, { email } = {}) {
      const em = email ?? state.activeUserEmail;
      clearCookieFor(state.currentVideo, em);
      commit('resetData');
    },
  },
  mutations: {
    setCurrentVideo(state, value) {
      state.currentVideo = value || [];
    },
    setTourFlags(state, value = {}) {
      state.isRegistroMuestras = Boolean(
        value.isRegistroMuestras
      );
      state.isFacturas = Boolean(value.isFacturas);
      state.isIncidencias = Boolean(value.isIncidencias);
      state.periodIndex =
        value.periodIndex === null || value.periodIndex === undefined
          ? null
          : value.periodIndex;
      state.videoIndex =
        value.videoIndex === null || value.videoIndex === undefined
          ? null
          : value.videoIndex;
    },
    setCounters(state, { showCount, maxShows }) {
      state.showCount = Number(showCount) || 0;
      state.maxShows = Number(maxShows) || 0;
    },
    setActiveUserEmail(state, email) {
      state.activeUserEmail = email || null;
    },
    resetData(state) {
      state.currentVideo = [];
      state.isRegistroMuestras = false;
      state.isFacturas = false;
      state.isIncidencias = false;
      state.periodIndex = null;
      state.videoIndex = null;
      state.showCount = 0;
      state.maxShows = 2;
      // activeUserEmail se mantiene
    },
  },
};
